import { inject, Injectable } from "@angular/core";
import { HelperFunctionService } from "./helper-function.service";
import { UserPersonalizationHelper } from "@techextensor/tab-core-utility";
import { ComponentType, Constants, SYSTEM_BUILT_IN_CUSTOM_VIEWS } from "../const/constants";
import { TabSdk } from "@techextensor/tab-sdk";
import { TranslationToken } from "../const/translation.token";
import { firstValueFrom, Observable, Subject } from "rxjs";

@Injectable({
    providedIn: 'root'
})
export class UserFavoritesService {
    public FAVORITES_KEY = SYSTEM_BUILT_IN_CUSTOM_VIEWS.FAVORITE_RECORDS_VIEW.Id;
    private isLoaded: boolean = false;
    private favoritesData: any = {};
    private favoritesRecord: any = null;
    private favoriteStateChanged = new Subject<{
        appObjId: string;
        recordId: string;
        isFavorite: boolean;
    }>();

    private helperFunctionService: HelperFunctionService = inject(HelperFunctionService);
    private userPersonalizationHelper: UserPersonalizationHelper = inject(UserPersonalizationHelper);

    /**
     * Loads the user's favorites data from the UserPersonalization table.
     * It uses the getUserPersonalization function to load the data and store it in the favoritesData property.
     * If the data is not found, it initializes the favoritesData property to an empty object.
     */
    private loadUserFavorites() {
        try {
            this.isLoaded = true;

            // Use common getUserPersonalization function
            const response = this.helperFunctionService.getUserPersonalization(
                null as any, // screenId
                null as any, // componentId  
                ComponentType.UserFavorites,
                this.FAVORITES_KEY // key
            );

            if (response?.length > 0) {
                // User personalization record was found, store it in the favoritesRecord property
                this.favoritesRecord = response[0];
                // Parse the Config property of the user personalization record and store it in the favoritesData property
                this.favoritesData = JSON.parse(this.favoritesRecord.Config || '{}');
            } else {
                // User personalization record was not found, initialize the favoritesData property to an empty object
                this.favoritesData = {};
            }
        } catch (error) {
            console.error('Error loading favorites:', error);
        }
    }

    /**
     * Get the count of favorite records for a specific app object
     * @param appObjId - The app object ID
     * @returns Number of favorite records for the app object
     */
    public getFavoriteCount(appObjId: string): number {
        if (!this.isLoaded) {
            this.loadUserFavorites();
        }

        return this.favoritesData?.[appObjId]?.length || 0;
    }

    /**
     * Checks if a record is a favorite of the user.
     * @param appObjId The ID of the app object.
     * @param recordId The ID of the record.
     * @returns True if the record is a favorite of the user, otherwise false.
     */
    public isFavorite(appObjId: string, recordId: string): boolean {
        if (!this.isLoaded) {
            this.loadUserFavorites();
        }

        // Check if the favorites data is defined for the given app object ID
        // and if it contains the given record ID.
        // If the record ID is found, return true, otherwise return false.
        return this.favoritesData?.[appObjId]?.includes(recordId) || false;
    }

    /**
     * Toggles a record as a favorite of the user.
     * @param appObjId The ID of the app object.
     * @param recordId The ID of the record.
     * @returns A promise that resolves to true if the record is added as a favorite, otherwise false.
     */
    public async toggleFavorite(appObjId: string, recordId: string) {
        if (!this.isLoaded) {
            this.loadUserFavorites();
        }

        // Check if the favorites data for the given app object ID is defined
        if (!this.favoritesData[appObjId]) {
            // If not, initialize it to an empty array
            this.favoritesData[appObjId] = [];
        }

        // Find the index of the record ID in the favorites data array
        const index = this.favoritesData[appObjId].indexOf(recordId);
        let isFavorite: boolean;

        // If the record ID is already in the array, remove it
        if (index > -1) {
            this.favoritesData[appObjId].splice(index, 1);
            isFavorite = false;
        } else {
            // Otherwise, add it to the array
            this.favoritesData[appObjId].push(recordId);
            isFavorite = true;
        }

        // Save the updated favorites data directly
        await this.saveFavorites();
        this.favoriteStateChanged.next({
            appObjId,
            recordId,
            isFavorite
        });
    }

    /**
     * Saves the user's favorite items to the UserPersonalization table.
     * If a record already exists, it updates the existing record.
     * If not, it inserts a new record.
     * It also refreshes the cache and displays a success or error message.
     * @returns A promise that resolves when the operation is complete.
     */
    private async saveFavorites(): Promise<void> {
        try {
            // Convert favorites data to JSON string
            const configData = JSON.stringify(this.favoritesData);

            if (this.favoritesRecord?.Id) {
                // Update the existing record if it already exists
                await TabSdk.crud.update(
                    {
                        appObjectIdOrName: Constants.TABD_UserPersonalization,
                        criteria: { Id: this.favoritesRecord.Id },
                        data: { Config: configData }
                    },
                    {
                        skipDefaultToast: true
                    }
                );
            } else {
                // Prepare data for insertion
                const data = {
                    Type: ComponentType.UserFavorites,
                    Key: this.FAVORITES_KEY,
                    Config: configData,
                    UserId: TabSdk.store.userId
                };

                // Insert new record if it does not exist
                const res: any = await TabSdk.crud.insert(
                    {
                        appObjectIdOrName: Constants.TABD_UserPersonalization,
                        data
                    },
                    {
                        skipDefaultToast: true
                    }
                );

                // Store the result in the favoritesRecord
                this.favoritesRecord = { ...data, Id: res?.Result };
            }

            // Refresh cache and IDB to ensure data consistency
            this.refreshUserPersonalizationData();

        } catch (error) {
            // Log error and display an error message to the user
            console.error('Error saving favorites:', error);
            TabSdk.ui.showError({ message: TranslationToken.TryAgainLater });
        }
    }

    /**
     * Refreshes the user personalized data in cache and IndexedDB.
     * This method is called after saving the user favorites to ensure that the data is consistent.
     * The same logic is used in the helper function service.
     * @returns {Promise<void>} A promise that resolves when the data has been refreshed.
     */
    private async refreshUserPersonalizationData(): Promise<void> {
        try {
            // Reload user personalized data (same as helper does)
            const userPersonalizationResponse: any = await firstValueFrom(
                this.helperFunctionService.getDSQData(Constants.Get_UserWise_Personalize_Data_Id)
            );

            // Set the fetched data in the user personalization helper
            this.userPersonalizationHelper.setUserPersonalizedData(userPersonalizationResponse?.Result ?? []);

        } catch (error) {
            console.error('Error refreshing personalization data:', error);
        }
    }

    /**
     * Returns an observable that emits the favorite state changes.
     * The emitted values are objects with the following properties:
     * - appObjId: The ID of the app object.
     * - recordId: The ID of the record.
     * - isFavorite: True if the record is a favorite of the user, otherwise false.
     * @returns {Observable<any>} An observable that emits the favorite state changes.
     */
    public getFavoriteStateChanged(): Observable<any> {
        return this.favoriteStateChanged.asObservable();
    }
}